import React, { useEffect, useMemo, useRef, useState } from 'react';

import { DEFAULT_ERROR, useCustomSnackBar } from 'hooks/useCustomSnackBar';
import { useReactTableState } from 'hooks/useReactTableState';
import useTableCurrentState from 'hooks/useTableCurrentState';

import {
    QueryResponse,
    useQueryGetSchedulingSimulationResults,
} from 'api/queries/getSchedulingSimulationResults.query';

import { TooltipPosition } from 'components/common/CustomizedTooltip/customizedTooltip.enums';
import Icon from 'components/common/Icons/icon';
import { CustomTableColumnsModal } from 'components/common/Modals/CustomTableColumns/customTableColumnsModal';
import NoData from 'components/common/NoData/noData';
import { TableV8 } from 'components/common/Tables/TableV8/tableV8';
import { serializeColumnsVisibility } from 'components/common/Tables/Tables.utils';
import { WidgetWithTitle } from 'components/common/WidgetWithTitle/WidgetWithTitle';
import { CustomPagination } from 'components/common/pagination/customPagination';

import { IRowsBoard } from 'components/Dashboards/Project/Components/CustomDashboard/utils/utils.type';
import { CustomizedButton, CustomizedRoundIcon, CustomizedTooltip } from 'components/common';
import { SimulatedActivitiesFilters } from 'components/widgets/ScheduleSimulation/components/SimulatedActivitiesTable/SimulatedActivitiesFilter/SimulatedActivitiesFilters';
import { useSimulatedActivitiesFiltersStore } from 'components/widgets/ScheduleSimulation/components/SimulatedActivitiesTable/SimulatedActivitiesFilter/simulatedActivitiesFilter.store';
import classes from 'components/widgets/ScheduleSimulation/components/SimulatedActivitiesTable/SimulatedTaskActivitiesTable/SimulatedActivitiesTable.module.scss';
import {
    buildPayloadFilter,
    columnsV8,
    initialState,
} from 'components/widgets/ScheduleSimulation/components/SimulatedActivitiesTable/SimulatedTaskActivitiesTable/SimulatedActivitiesTable.utils';
import { SortSelectorComponent } from 'components/widgets/ScheduleSimulation/components/SimulatedActivitiesTable/SortSelectorComponent/sortSelectorComponent';
import {
    SortValueOptions,
    sortByFinishDateVariance,
    sortByStatus,
    sortedByColumn,
    sortingOptions,
} from 'components/widgets/ScheduleSimulation/components/SimulatedActivitiesTable/SortSelectorComponent/sortSelectorComponent.utils';
import { useSimulationCacheKeyStore } from 'components/widgets/ScheduleSimulation/components/SimulatedActivitiesTable/simulationCacheKey.store';
import { SettingsAndWarnings } from 'components/widgets/ScheduleSimulation/components/SimulationWarning/SimulationWarning';
import { ExportSimulationModal } from 'components/widgets/ScheduleSimulation/components/exportModal/ExportSimulationModal';

interface Props extends Omit<IRowsBoard, 'filterData' | 'widgetId'> {
    simulationDate: number;
    metaDataId: number;
    setNote: React.Dispatch<React.SetStateAction<string>>;
    setNotification: React.Dispatch<React.SetStateAction<SettingsAndWarnings | null>>;
}

const SimulatedActivitiesTable = ({
    metaDataId,
    externalComponents,
    simulationDate,
    setNote,
    setNotification,
}: Props) => {
    const {
        pagination,
        setPagination,
        sorting,
        setSorting,
        columnVisibility,
        setColumnVisibility,
        columnOrder,
        setColumnOrder,
        rowSelection,
        setRowSelection,
    } = useReactTableState({
        initialPagination: {
            size: 50,
            page: 1,
        },
        initialSorting: [{ id: 'statusChange', desc: false }],
    });
    const [showExportModal, setShowExportModal] = useState(false);
    const { sessionKey, cacheIsReady } = useSimulationCacheKeyStore();
    const [sortSelector, setSortSelector] = useState<ISelectOption<SortValueOptions>>(sortByStatus);
    const { isCriticalPathChangedFilter, isCriticalPathFilter, statusFilter, activityTypeFilter, search } =
        useSimulatedActivitiesFiltersStore();

    const { handleSnackBar } = useCustomSnackBar();

    const resetSortSelector = () => {
        setSortSelector(sortedByColumn);
    };

    const filters = useMemo(
        () =>
            buildPayloadFilter(
                search,
                statusFilter,
                isCriticalPathFilter,
                isCriticalPathChangedFilter,
                activityTypeFilter,
            ),
        [search, statusFilter, isCriticalPathFilter, isCriticalPathChangedFilter, activityTypeFilter],
    );

    const [modalColumnsOpen, setModalColumnsOpen] = useState<boolean>(false);
    const [totalPages, setTotalPages] = useState(0);

    const { data, isLoading, isFetching, error } = useQueryGetSchedulingSimulationResults({
        metaDataId,
        filter: {
            filters: filters,
        },
        simulationDate: simulationDate,
        paging: {
            ...pagination,
            page: pagination.page - 1,
        },
        sorting: sorting,
        key: sessionKey,
        cacheIsReady,
    });

    const tableData = useMemo(() => {
        if (data && data.changedActivities && data.changedActivities.length > 0) {
            return data.changedActivities.map((activity) => {
                const backgroundColorClass = activity.modifiedByUser
                    ? classes.statusChanged
                    : classes.statusHasNotChanged;
                return {
                    ...activity,
                    className: backgroundColorClass,
                };
            });
        }

        return [];
    }, [data]);

    const boundaryRef = useRef<HTMLDivElement>(null);
    const componentKey = 'SimulationTaskActivitiesTable';
    const updatedInitialState = useTableCurrentState({ initialState, componentKey });
    const title = 'Simulation Summary';

    const handleExportModalOpen = () => {
        setShowExportModal(true);
    };

    useEffect(() => {
        updatedInitialState && setColumnOrder(updatedInitialState.columnOrder ?? []);
        updatedInitialState &&
            setColumnVisibility(
                serializeColumnsVisibility({ columns: columnsV8, hiddenColumns: updatedInitialState.hiddenColumns }) ??
                    {},
            );
    }, [updatedInitialState]);

    const titleWithCount = useMemo(() => {
        if (data != undefined && data.totalChanges > 0) return `${title} (${data.totalChanges})`;
        return `${title} (0)`;
    }, [title, data]);

    const tableInstance = useMemo(() => {
        const hiddenColumns = Object.keys(columnVisibility).reduce((acc: string[], item: string) => {
            columnVisibility[item] === false && acc.push(item);
            return acc;
        }, []);

        return {
            setColumnOrder,
            setHiddenColumns: (hiddenColumns: string[]) =>
                setColumnVisibility(serializeColumnsVisibility({ columns: columnsV8, hiddenColumns })),
            state: {
                hiddenColumns,
            },
            allColumns: columnOrder
                .map((item) => {
                    const column = columnsV8.find((column) => column.id === item);
                    if (column && column.id) {
                        return {
                            ...column,
                            isVisible: !hiddenColumns.includes(column.id),
                        };
                    }
                    return null;
                })
                .filter((x) => x),
        };
    }, [columnVisibility, columnOrder]);

    // Pagination refers to the UI pagination. For API the pagination we will subtract 1 from pagination.page.
    useEffect(() => {
        setPagination({ ...pagination, page: 1 });
        if (sorting.length > 0) {
            if (sorting[0].id !== sortByFinishDateVariance.value) {
                const sortSelectorValues = ['rank', 'statusChange', 'finishVariance'];
                if (sorting.length > 0 && !sortSelectorValues.includes(sorting[0].id)) {
                    resetSortSelector();
                }
            } else {
                if (sorting[0].id === sortByFinishDateVariance.value && sorting[0].desc === false) {
                    resetSortSelector();
                }
                // sorting === finishVariance
                const sortSelectorValues = ['rank', 'statusChange'];
                if (sortSelectorValues.includes(sorting[0].id)) {
                    resetSortSelector();
                }
            }
            // no sort. If dropdown selector was finish variance and someone clicked finish variance's column then we reset the dropdown
        } else if (sortSelector.value === sortByFinishDateVariance.value) {
            resetSortSelector();
        }
    }, [filters, sorting]);

    useEffect(() => {
        if (data) {
            if (data.changedActivities && data.changedActivities.length > 0) {
                setTotalPages(data.totalPages);
            } else {
                setTotalPages(0);
            }
            setNote(data.note ?? '');
            setNotification({
                warnings: data.warnings ?? [],
                settings: data.settings ?? [],
            });
        }
    }, [data]);

    useEffect(() => {
        if (error !== null && error.response) {
            DEFAULT_ERROR({ error, handleError: handleSnackBar });
        }
    }, [error]);

    return (
        <WidgetWithTitle
            className={classes.simulationContainer}
            tooltip={data?.info}
            id={componentKey}
            title={titleWithCount}
            titleFilters={
                <div className={classes.titleFilters}>
                    <SimulatedActivitiesFilters />
                    <SortSelectorComponent
                        setSorting={setSorting}
                        setSortSelector={setSortSelector}
                        sortingOptions={sortingOptions}
                        sortSelector={sortSelector}
                    />
                </div>
            }
            titleComponents={[
                <CustomizedButton
                    key={'export'}
                    disabled={isLoading || isFetching || error !== null || !cacheIsReady}
                    size={'large'}
                    color={'primary'}
                    onClick={() => handleExportModalOpen()}
                >
                    Save Simulation Output As...
                </CustomizedButton>,
                externalComponents && <div key={'externalComponents'}>{externalComponents}</div>,
                <CustomizedTooltip
                    key={'tableColumns'}
                    tooltipPosition={TooltipPosition.Top}
                    tooltipContent={'Customize table columns'}
                    triggerElement={
                        <CustomizedRoundIcon enableHover={true} onClick={() => setModalColumnsOpen(true)}>
                            <Icon name={'tune-filter'} />
                        </CustomizedRoundIcon>
                    }
                />,
            ]}
        >
            <div className={classes.simulationWrapper} ref={boundaryRef}>
                <TableV8<QueryResponse['changedActivities'][0]>
                    data={tableData}
                    columns={columnsV8}
                    isLoading={isLoading || isFetching || !cacheIsReady}
                    noData={<NoData>No activities match the applied filter</NoData>}
                    sorting={sorting}
                    setSorting={setSorting}
                    columnVisibility={columnVisibility}
                    setColumnVisibility={setColumnVisibility}
                    columnOrder={columnOrder}
                    setColumnOrder={setColumnOrder}
                    rowSelection={rowSelection}
                    setRowSelection={setRowSelection}
                />
                <div className={classes.pagination}>
                    <CustomPagination
                        totalPages={totalPages}
                        pagination={pagination}
                        setPagination={setPagination}
                        isDisabled={isFetching || isLoading}
                    />
                </div>
            </div>

            <CustomTableColumnsModal
                onHideModal={() => setModalColumnsOpen(false)}
                showModal={modalColumnsOpen}
                componentKey={componentKey}
                tableInstance={tableInstance}
                initialState={initialState}
                columns={columnsV8}
                onChangeOrder={setColumnOrder}
                onChangeVisibility={setColumnVisibility}
            />

            <ExportSimulationModal
                metaDataId={metaDataId}
                onHideModal={() => setShowExportModal(false)}
                showModal={showExportModal}
                simulationDate={simulationDate}
            />
        </WidgetWithTitle>
    );
};

export default React.memo(SimulatedActivitiesTable);
